import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Pagination } from 'antd';

import { getJobActionsPermissions } from 'components/JobsTable/utils/getJobActionsPermissions';
import CardActionsMenu from 'components/SelectCardWrapper/CardActionsMenu';
import SidebarNavigation from 'components/SidebarNavigation';
import StyledSwitch from 'components/StyledSwitch';

import { selectActiveUserId } from 'features/applications/applicationsSelectors';
import { generateParams } from 'features/filters/filtersSlice';
import { selectJobs } from 'features/jobs/jobsSelectors';
import jobseekersService from 'features/jobseekers/jobseekersService';
import { selectAppLocale } from 'features/locale/localeSelectors';
import { selectIsBlockSidebarUrlHandling } from 'features/sidebar/sidebarSelectors';
import { setActiveUserId } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import usePageviewEvent from 'hooks/usePageViewEvent';
import { useSyncJobRoute } from 'hooks/useSyncJobRoute';

import { GTMEventLogger } from 'utils/analytics';
import { getDisplayJobTitle } from 'utils/jobUtils';

import { Job } from 'types/jobs';
import { JobseekerMatchesData } from 'types/jobseekers';

import { GTMEvents, SidebarContentTypes } from 'constants/enums';
import { TOUR_STEP_TARGET_JOB_MATCHES_TAB } from 'constants/tour';
import { PAGE_SIZE } from 'constants/uiSettings';

import ContentBlock from './ContentBlock/ContentBlock';
import styles from './MatchesView.module.scss';

const DEFAULT_INITIAL_PAGE = 1;

const MatchesView = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(DEFAULT_INITIAL_PAGE);
  const [isLoadingJobseekers, setIsLoadingJobseekers] = useState(false);
  const [isAIMatches, setIsAIMatches] = useState(false);
  const [isChangingMode, setIsChangingMode] = useState(false);
  const catchError = useErrorCatch();
  const locale = useSelector(selectAppLocale);
  const jobId = useSelector(selectActiveUserId);
  const isBlockUrlHandling = useSelector(selectIsBlockSidebarUrlHandling);
  const jobs = useSelector(selectJobs);
  const job = jobs.find((job: Job) => job.id === jobId) || null;
  const [jobseekersMatchesDataList, setJobseekersMatchesDataList] = useState<
    JobseekerMatchesData[] | undefined
  >(undefined);
  const [totalElements, setTotalElements] = useState(0);
  const jobActionsPermissions = getJobActionsPermissions(t, job);

  useSyncJobRoute(jobId, isBlockUrlHandling);
  usePageviewEvent(GTMEvents.JobListViewSidebarMatching);

  const generateRequestParams = useCallback(
    (jobId: string) => {
      const baseParams = {
        page: currentPage - 1,
        pageSize: PAGE_SIZE
      };

      return isAIMatches
        ? generateParams({ ...baseParams, jobId, locale })
        : generateParams(baseParams);
    },
    [isAIMatches, currentPage, locale]
  );

  const fetchJobseekersData = useCallback(
    async (jobId: string, params: string) => {
      return isAIMatches
        ? await jobseekersService.getRecommendedJobseekers(params)
        : await jobseekersService.getJobseekersMatchesJob(jobId, params);
    },
    [isAIMatches]
  );

  const updateJobseekersState = useCallback((result: any) => {
    setJobseekersMatchesDataList(result?.content);
    setTotalElements(result?.totalElements || 0);
  }, []);

  const handleFetchError = useCallback(
    (error: any) => {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.failedFetchJobseekersMatches')
        }
      });
    },
    [catchError]
  );

  const finishLoading = useCallback(() => {
    setIsLoadingJobseekers(false);
    setIsChangingMode(false);
  }, []);

  const fetchJobseekersMatches = useCallback(
    async (jobId: string): Promise<void> => {
      try {
        setIsLoadingJobseekers(true);

        const params = generateRequestParams(jobId);
        const result = await fetchJobseekersData(jobId, params);

        const isMatchesLoaded = result?.content?.length;
        if (isMatchesLoaded) {
          GTMEventLogger(
            GTMEvents.AiJobsListViewSidebarMatchingRecommendationsLoaded
          );
        }

        updateJobseekersState(result);
      } catch (error) {
        handleFetchError(error);
      } finally {
        finishLoading();
      }
    },
    [
      generateRequestParams,
      fetchJobseekersData,
      updateJobseekersState,
      handleFetchError,
      finishLoading
    ]
  );

  useEffect(() => {
    if (!jobId) {
      return;
    }
    fetchJobseekersMatches(jobId);
  }, [jobId, isAIMatches, currentPage, fetchJobseekersMatches]);

  const handleModeChange = useCallback(
    (checked: boolean) => {
      if (isChangingMode) {
        return;
      }
      setIsChangingMode(true);
      setIsAIMatches(checked);
      if (currentPage !== DEFAULT_INITIAL_PAGE) {
        setCurrentPage(DEFAULT_INITIAL_PAGE);
      }
    },
    [isAIMatches, currentPage, isChangingMode]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [currentPage, isAIMatches]
  );

  const handleSetInviteSent = (jobseekerId: string) => {
    GTMEventLogger(GTMEvents.AiJobsListViewSidebarMatchingRecommendationInvite);

    const updatedList = jobseekersMatchesDataList?.map((item) => {
      if (item.jobseekerId === jobseekerId) {
        return { ...item, inviteSent: true };
      }

      return item;
    });

    setJobseekersMatchesDataList(updatedList);
  };

  const shouldShowPagination =
    jobseekersMatchesDataList && jobseekersMatchesDataList?.length > 0;

  return (
    <aside>
      <SidebarNavigation id={jobId} data={jobs} action={setActiveUserId} />

      <div id={TOUR_STEP_TARGET_JOB_MATCHES_TAB}>
        <div className={styles.header}>
          {job && (
            <div className={styles.title}>
              {getDisplayJobTitle(job) || t('job.defaultTitle')}
            </div>
          )}

          <CardActionsMenu
            sidebarContentType={SidebarContentTypes.JobView}
            permissions={jobActionsPermissions}
            contentId={job?.id}
          />
        </div>
        <div className={styles.switchContainer}>
          <StyledSwitch
            leftLabel={t('sidebar.job.match.skillsetMatch')}
            rightLabel={t('sidebar.job.match.aiInsight')}
            isDefaultChecked={isAIMatches}
            onChange={handleModeChange}
            disabled={isChangingMode}
          />
        </div>
      </div>

      <div className={styles.cardsWrapper}>
        <ContentBlock
          isLoadingJobseekers={isLoadingJobseekers}
          jobId={jobId}
          job={job}
          jobseekersMatchesDataList={jobseekersMatchesDataList}
          handleSetInviteSent={handleSetInviteSent}
          isAIMatches={isAIMatches}
        />
      </div>
      {shouldShowPagination && (
        <div className={styles.pagination}>
          <Pagination
            current={currentPage}
            total={totalElements}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            onChange={handlePageChange}
            responsive
          />
        </div>
      )}
    </aside>
  );
};

export default MatchesView;
